// src/pages/pentest/report/ExecutiveReport.jsx
// Executive Report — branded to PRIMARYCOLOR/COMPANY with cyber/futuristic accents (print-safe)
import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  Grid,
  Chip,
  Divider,
  Avatar,
  Stack,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';
import PublicIcon from '@mui/icons-material/Public';
import DnsIcon from '@mui/icons-material/Dns';
import RouteIcon from '@mui/icons-material/Route';
import LanIcon from '@mui/icons-material/Lan';
import LinkIcon from '@mui/icons-material/Link';

// cyber vibe icons
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import InsightsIcon from '@mui/icons-material/Insights';
import RadarIcon from '@mui/icons-material/Radar';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TimelineIcon from '@mui/icons-material/Timeline';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline';

import { useParams, useNavigate } from 'react-router-dom';
import Nav from '../nav';
import { BASEURL, PRIMARYCOLOR, COMPANY } from '../../../Constants';
import html2pdf from 'html2pdf.js';
import html2canvas from 'html2canvas';
import WhiteLabelForm from '../../../components/whiteLabelForm';
import './ReportsPage.css';
window.html2canvas = html2canvas;

const DEFAULT_PRIMARY = PRIMARYCOLOR || '#005CC5';
const DEFAULT_COMPANY = COMPANY || 'StealthNet AI';
const DEFAULT_CLIENT = '';

/** ---------- Helpers ---------- **/
const getGrade = (score) => {
  if (score == null) return '?';
  if (score < 60) return 'F';
  if (score < 70) return 'D';
  if (score < 80) return 'C';
  if (score < 90) return 'B';
  if (score <= 95) return 'A-';
  return 'A+';
};

const getGradeColor = (grade) => {
  switch (grade) {
    case 'A+':
    case 'A-': return '#2e7d32';
    case 'B':  return '#1565c0';
    case 'C':  return '#ef6c00';
    case 'D':  return '#fbc02d';
    case 'F':  return '#c62828';
    default:   return PRIMARYCOLOR;
  }
};
const gradeMessages = {
  'A+': 'Outstanding security posture. No material risk identified.',
  'A-': 'Strong posture with minor opportunities for hardening.',
  B: 'Good overall, address medium/low issues to improve resilience.',
  C: 'Fair posture. Prioritize remediation of multiple findings.',
  D: 'Weak posture. Significant vulnerabilities need attention.',
  F: 'Critical risk. Immediate remediation is required.',
};

const severityOrder = ['Critical', 'High', 'Medium', 'Low', 'Info'];
const sevColorMap = {
  Critical: '#6a1b9a',
  High:     '#c62828',
  Medium:   '#ef6c00',
  Low:      '#1565c0',
  Info:     '#2e7d32',
};

// Normalize anything coming from API into one of: Critical/High/Medium/Low/Info
const normalizeSeverity = (sev) => {
  if (!sev) return 'Medium';

  const s = String(sev).trim().toLowerCase();

  if (s.startsWith('crit')) return 'Critical';
  if (s.startsWith('high')) return 'High';
  if (s.startsWith('med'))  return 'Medium';
  if (s.startsWith('low'))  return 'Low';

  // supports: "Info", "Informational", "Information"
  if (s.startsWith('info') || s.startsWith('inform')) return 'Info';

  return 'Medium';
};

// Prefer API field `severity`, fallback to parsing report_details if needed
const extractSeverity = (exploitOrTxt) => {
  if (!exploitOrTxt) return 'Medium';

  // If they passed the full exploit object
  if (typeof exploitOrTxt === 'object') {
    if (exploitOrTxt.severity) return normalizeSeverity(exploitOrTxt.severity);
    if (exploitOrTxt.report_details) return extractSeverity(exploitOrTxt.report_details);
    return 'Medium';
  }

  // Fallback: parse from markdown/text if someone passes report_details directly
  if (typeof exploitOrTxt === 'string') {
    const m = exploitOrTxt.match(
      /(?:^|\n)#{1,3}\s*Severity[\s:]*\n?\s*(Critical|High|Medium|Low|Info)\b/i
    );
    if (m?.[1]) return normalizeSeverity(m[1]);
  }

  return 'Medium';
};


const stripMarkdownText = (value = '') =>
  String(value)
    .replace(/!\[.*?\]\(.*?\)/g, '')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/[*_`~]/g, '')
    .replace(/<[^>]+>/g, '')
    .replace(/^#+\s*/, '')
    .replace(/^\s*[-+*]\s+/, '')
    .replace(/\s+/g, ' ')
    .trim();

const genericFindingLabels = new Set([
  'name',
  'title',
  'finding',
  'finding name',
  'description',
  'details',
  'location',
  'remediation',
  'remediation steps',
  'recommendation',
  'recommendations',
  'impact',
  'severity',
  'evidence',
  'proof',
  'references',
  'endpoint',
  'endpoints',
  'asset',
  'assets',
  'host',
  'hosts',
  'summary',
]);

const isPlaceholderFindingTitle = (value = '') => {
  const cleaned = stripMarkdownText(value).toLowerCase().replace(/:$/, '');
  return !cleaned
    || genericFindingLabels.has(cleaned)
    || ['n/a', 'na', 'none', 'unknown'].includes(cleaned);
};

const extractNamedSectionValue = (txt = '') => {
  const match = String(txt).match(
    /(?:^|\n)#{1,6}\s*(?:Name|Title|Finding Name)\s*\n+([\s\S]*?)(?=\n#{1,6}\s+|$)/i
  );

  if (!match?.[1]) return '';

  return match[1]
    .split(/\r?\n/)
    .map(stripMarkdownText)
    .find((line) => line && !isPlaceholderFindingTitle(line)) || '';
};

const compactMeta = (value = '', maxLength = 56) => {
  const cleaned = stripMarkdownText(value);
  if (!cleaned) return '';
  return cleaned.length > maxLength ? `${cleaned.slice(0, maxLength - 1).trim()}…` : cleaned;
};

const extractTitle = (txt = '', fallback = '') => {
  const safeFallback = stripMarkdownText(fallback);
  if (safeFallback && !isPlaceholderFindingTitle(safeFallback)) return safeFallback;

  const namedSection = extractNamedSectionValue(txt);
  if (namedSection) return namedSection;

  const headingMatches = Array.from(String(txt).matchAll(/^\s*#{1,6}\s+(.+)$/gm));
  for (const match of headingMatches) {
    const heading = stripMarkdownText(match[1]);
    if (heading && !isPlaceholderFindingTitle(heading)) return heading;
  }

  const firstMeaningfulLine = String(txt)
    .split(/\r?\n/)
    .map(stripMarkdownText)
    .find((line) => line && !isPlaceholderFindingTitle(line));

  return firstMeaningfulLine || safeFallback || 'Finding';
};

// Date pretty
const fmtDate = (d) =>
  new Date(d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });

/** ---------- Simple scope parsing (domain/IP/CIDR) ---------- **/
const ipv4Re = /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;
const cidrRe = /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}\/([0-9]|[12]\d|3[0-2])$/;
const domainRe = /^(?=.{1,253}$)(([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63})$/i;
const classifyAsset = (s) => {
  const v = (s || '').trim();
  if (!v) return { type: 'unknown', value: v, raw: s };
  if (cidrRe.test(v)) return { type: 'cidr', value: v, raw: s };
  if (ipv4Re.test(v)) return { type: 'ip', value: v, raw: s };
  const noProto = v.replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
  if (noProto.startsWith('*.')) {
    const base = noProto.replace(/^\*\./, '');
    if (domainRe.test(base)) return { type: 'domain', value: base, raw: s, wasWildcard: true };
  }
  if (domainRe.test(noProto)) return { type: 'domain', value: noProto, raw: s };
  return { type: 'unknown', value: v, raw: s };
};

const typeIcon = (t) => {
  switch (t) {
    case 'domain': return <PublicIcon fontSize="small" />;
    case 'ip':     return <DnsIcon fontSize="small" />;
    case 'cidr':   return <RouteIcon fontSize="small" />;
    default:       return <LanIcon fontSize="small" />;
  }
};

/** ---------- Component ---------- **/
const ExecutiveReport = ({ minimal = false, primary=PRIMARYCOLOR,  company=COMPANY }) => {
  const { pentest_id } = useParams();
  const navigate = useNavigate();
  const reportRef = useRef(null);

  const [stats, setStats] = useState(null);
  const [exploits, setExploits] = useState([]);
  const [execSummary, setExecSummary] = useState('');
  const [scopeRaw, setScopeRaw] = useState('');
  const [loading, setLoading] = useState(true);

  const [primaryColors_, setPrimaryColor] = useState(primary);
  const [companyName, setCompanyName]   = useState(company);
  const [clientName, setClientName]     = useState(''); // optional seed from API if you want
  
  // tiny handler the form can call
  const applyWhiteLabel = ({ primary, company, client }) => {
    if (primary !== undefined) setPrimaryColor(primary);
    if (company !== undefined) setCompanyName(company);
    if (client  !== undefined) setClientName(client);
  };

  useEffect(() => { setPrimaryColor(primary); }, [primary]);
  useEffect(() => { setCompanyName(company); }, [company]);
  const brandPrimary = primaryColors_;
  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) return navigate('/');

    Promise.all([
      fetch(`${BASEURL}/pentest/stats?pentest_id=${pentest_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => r.json()),
      fetch(`${BASEURL}/pentest/exploits?pentest_id=${pentest_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => r.json()),
      fetch(`${BASEURL}/pentest/report/executive?pentest_id=${pentest_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => r.json()),
      fetch(`${BASEURL}/pentest/scope?pentest_id=${pentest_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => r.json()).catch(()=>({ scope:'' })),
    ])
      .then(([statsRes, exploitsRes, summaryRes, scopeRes]) => {
        if ([statsRes, exploitsRes].some((r) => r?.error)) {
          if ([statsRes?.message, exploitsRes?.message].includes('Invalid token')) {
            return navigate('/');
          }
        }
        setStats(statsRes?.stats || statsRes);
        setExploits(exploitsRes?.exploits || []);
        setExecSummary(summaryRes?.report || '');
        setScopeRaw(scopeRes?.scope || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [navigate, pentest_id]);

  if (loading || !stats) {
    return (
      <Box sx={{ display: 'flex', height: minimal ? 'auto' : '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // brand palette
  const p = brandPrimary;
  const pDark = darken(p, 0.25);
  const pMid  = darken(p, 0.12);
  const pLite = lighten(p, 0.28);
  const ink   = '#0f172a';
  const brand = { p, pDark, pMid, pLite, ink };

  // Executive summary paragraphs
  const paras = execSummary.split(/\n{2,}/g).map((p) => p.trim()).filter(Boolean);

  // Grade & meta
  const riskScore   = stats?.risk_score ?? stats?.score ?? null;
  const grade       = getGrade(riskScore);
  const gradeColor  = getGradeColor(grade);
  const counts      = stats?.counts || {};
  const totalFindings = counts?.exploits ?? counts?.findings ?? 0;
  const openPorts     = counts?.open_ports ?? 0;
  const totalAssets   = counts?.assets ?? counts?.hosts ?? 0;

  // Severity counts (ordered)
  const sevCount = severityOrder.reduce((acc, k) => ({ ...acc, [k]: 0 }), {});
  exploits.forEach((e) => {
    const sev = extractSeverity(e);
    sevCount[sev] = (sevCount[sev] || 0) + 1;
  });

  const countedFindings = severityOrder.reduce((sum, label) => sum + (sevCount[label] || 0), 0);
  const visualFindingsTotal = Math.max(totalFindings, countedFindings);
  const highImpactCount = (sevCount.Critical || 0) + (sevCount.High || 0);
  const highImpactShare = visualFindingsTotal ? Math.round((highImpactCount / visualFindingsTotal) * 100) : 0;
  const maxSeverityCount = Math.max(1, ...severityOrder.map((label) => sevCount[label] || 0));
  const severityStats = severityOrder.map((label) => {
    const value = sevCount[label] || 0;
    return {
      label,
      value,
      color: sevColorMap[label],
      share: visualFindingsTotal ? Math.round((value / visualFindingsTotal) * 100) : 0,
    };
  });

  // Top 5 findings
  const sevRank = { Critical: 5, High: 4, Medium: 3, Low: 2, Info: 1 };
  const topFindings = [...exploits]
    .map((e, i) => ({
      id: e?.id ?? i,
      title: extractTitle(e?.report_details || '', e?.name || e?.title || `Finding ${i + 1}`),
      severity: extractSeverity(e),
      asset: compactMeta(e?.impacted_assets || e?.endpoint || e?.host || e?.asset || ''),
    }))
    .sort((a, b) => (sevRank[b.severity] || 0) - (sevRank[a.severity] || 0))
    .slice(0, 5);

  // Scope (compact)
  const scopeLines = String(scopeRaw || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const scopeAssets = scopeLines.map(classifyAsset);
  const scopeTotals = scopeAssets.reduce(
    (acc, a) => ({ ...acc, [a.type]: (acc[a.type] || 0) + 1 }),
    { domain: 0, ip: 0, cidr: 0, unknown: 0 }
  );
  const scopePreview = scopeAssets.slice(0, 6);

  // PDF (standalone page usage)
  const handleDownloadPDF = () => {
    const opt = {
      margin: [12, 10, 14, 10],
      filename: `Executive_Report_${pentest_id}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: 'pt', format: 'letter', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] },
    };
    html2pdf().set(opt).from(reportRef.current).save();
  };

  /** ---------- Minimal (embeddable) ---------- **/
  if (minimal) {
    return (
      <Box className="report-surface" sx={{ maxWidth: '8.5in', mx: 'auto', py: 4, px: 3 }}>
        <div ref={reportRef} data-exec-root>
          <Header
            grade={grade}
            gradeColor={gradeColor}
            riskScore={riskScore}
            stats={stats}
            pentest_id={pentest_id}
            brand={brand}
            company={companyName}
            clientName={clientName}
          />
          <MainBody
            {...{
              totalFindings: visualFindingsTotal,
              openPorts,
              totalAssets,
              severityStats,
              maxSeverityCount,
              highImpactCount,
              highImpactShare,
              topFindings,
              paras,
              scopePreview,
              scopeTotals,
            }}
            brand={brand}
          />
        </div>
      </Box>
    );
  }

  /** ---------- Full page with Nav + Download ---------- **/
  return (
    <>
      <Box className="no-print" sx={{ position: 'relative' }}>
        <Nav />
        <Button
          variant="contained"
          onClick={handleDownloadPDF}
          sx={{
            position: 'absolute',
            top: 8,
            right: 16,
            backgroundColor: DEFAULT_PRIMARY,
            '&:hover': {
              backgroundColor: darken(DEFAULT_PRIMARY, 0.12),
            },
          }}
        >
          Download PDF
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card className="report-config-card" sx={{ width: '100%', maxWidth: '8.5in', mb: 2, boxShadow: 'none', border: '1px solid rgba(0,0,0,0.08)' }}>
          <WhiteLabelForm
            defaultPrimary={DEFAULT_PRIMARY}
            defaultCompany={DEFAULT_COMPANY}
            defaultClient={DEFAULT_CLIENT}
            onApply={applyWhiteLabel}
            showCompany
            showClient
            title="White Label"
          />
        </Card>
      </Box>

      <Box className="report-platform-shell" sx={{ minHeight: '100vh', pt: 2, pb: 6, px: 2 }}>
        <Box className="pdf-page exec-page" sx={{ display: 'flex', justifyContent: 'center' }} data-report-section="executive">
          <Card className="report-page-card report-page-card--standard exec-wrapper allow-split" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <Box
              ref={reportRef}
              data-exec-root
              sx={{
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                px: '0.72in',
                py: '0.68in',
              }}
            >
            <Header
              grade={grade}
              gradeColor={gradeColor}
              riskScore={riskScore}
              stats={stats}
              pentest_id={pentest_id}
              brand={brand}
              company={companyName}
              clientName={clientName}
            />
            <MainBody
              {...{
                totalFindings: visualFindingsTotal,
                openPorts,
                totalAssets,
                severityStats,
                maxSeverityCount,
                highImpactCount,
                highImpactShare,
                topFindings,
                paras,
                scopePreview,
                scopeTotals,
              }}
              brand={brand}
            />
            </Box>
          </Card>
        </Box>
      </Box>
    </>
  );
};

/** ---------- Subcomponents (branded, cyber accents) ---------- **/
const Header = ({ grade, gradeColor, riskScore, stats, pentest_id, brand, company, clientName }) => {
  const { p, pMid, pDark, pLite, ink } = brand;
  const org = clientName || stats?.meta?.client_name || stats?.client || 'Client';
  const testingType = stats?.meta?.type || 'External Network';
  const reportDate = fmtDate(stats?.meta?.generated_at || Date.now());

  return (
    <Card
      data-exec-block="header"
      className="avoid-break"
      sx={{
        p: 2.5,
        mb: 2.5,
        boxShadow: 0,
        border: `1px solid ${alpha(ink, 0.08)}`,
        bgcolor: '#fff',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* neon top rail */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 6,
          background: `linear-gradient(90deg, ${pDark}, ${pMid} 60%, ${pLite})`,
          '@media print': { background: pMid },
        }}
      />
      <Grid container spacing={1.5} alignItems="center">
        <Grid item xs={12} md={7} className="exec-header-primary">
          <Stack direction="row" spacing={1.6} alignItems="center">
            <Avatar
              variant="rounded"
              sx={{
                width: 52,
                height: 52,
                bgcolor: alpha(p, 0.12),
                color: pMid,
                border: `1px solid ${alpha(p, 0.28)}`,
                fontWeight: 900,
              }}
            >
              <SecurityIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.2, color: ink }}>
                Executive Security Assessment
              </Typography>
              <Typography variant="body2" sx={{ color: alpha(ink, 0.7) }}>
                {org} • {testingType} • ID #{pentest_id}
              </Typography>
              <Typography variant="body2" sx={{ color: alpha(ink, 0.7) }}>
                {reportDate}
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.8, flexWrap: 'wrap' }}>
                <Chip
                  icon={<VerifiedUserIcon />}
                  size="small"
                  label={company}
                  sx={{
                    bgcolor: alpha(p, 0.08),
                    color: pMid,
                    border: `1px solid ${alpha(p, 0.2)}`,
                    fontWeight: 800,
                    borderRadius: '999px',
                  }}
                />
                <Chip
                  icon={<InsightsIcon />}
                  size="small"
                  label="Threat-informed"
                  sx={{
                    bgcolor: alpha(pLite, 0.12),
                    color: pMid,
                    border: `1px solid ${alpha(p, 0.2)}`,
                    fontWeight: 700,
                    borderRadius: '999px',
                  }}
                />
                <Chip
                  icon={<RadarIcon />}
                  size="small"
                  label="Active Recon"
                  sx={{
                    bgcolor: alpha(pDark, 0.12),
                    color: pMid,
                    border: `1px solid ${alpha(p, 0.2)}`,
                    fontWeight: 700,
                    borderRadius: '999px',
                  }}
                />
              </Stack>
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} md={5} className="exec-header-grade">
          <Box className="exec-header-grade-box" sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Button
              variant="contained"
              sx={{ bgcolor: gradeColor, color: '#fff', fontWeight: 900, px: 1.25, borderRadius: '12px' }}
              startIcon={<PieChartOutlineIcon />}
            >
              GRADE: {grade}
            </Button>
            <Typography variant="caption" sx={{ display: 'block', color: alpha(ink, 0.7), mt: 0.5 }}>
              Risk Score: <strong>{riskScore ?? '—'}</strong>
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: alpha(ink, 0.7) }}>
              {gradeMessages[grade]}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

const Kpi = ({ label, value, hint, footnote, icon, accent, brand }) => {
  const { p, ink } = brand;
  const tone = accent || p;

  return (
    <Card
      className="avoid-break kpi-card"
      sx={{
        p: 1.4,
        minHeight: 118,
        border: `1px solid ${alpha(ink, 0.08)}`,
        boxShadow: 0,
        background: `linear-gradient(180deg, ${alpha(tone, 0.12)} 0%, rgba(255,255,255,0.98) 58%)`,
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 5,
          background: `linear-gradient(90deg, ${alpha(tone, 0.96)}, ${alpha(tone, 0.6)})`,
        }}
      />
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{ color: alpha(ink, 0.62), fontWeight: 900, letterSpacing: 1.25, lineHeight: 1 }}
          >
            {label}
          </Typography>
          <Typography sx={{ mt: 0.55, fontSize: '1.7rem', fontWeight: 900, lineHeight: 1, color: ink }}>
            {value}
          </Typography>
        </Box>
        <Avatar
          sx={{
            width: 34,
            height: 34,
            bgcolor: alpha(tone, 0.12),
            color: tone,
            border: `1px solid ${alpha(tone, 0.22)}`,
            boxShadow: `0 6px 18px ${alpha(tone, 0.16)}`,
          }}
        >
          {icon}
        </Avatar>
      </Stack>

      <Divider sx={{ my: 0.85, borderColor: alpha(tone, 0.12) }} />

      <Typography variant="body2" sx={{ color: alpha(ink, 0.84), lineHeight: 1.32, fontWeight: 600, fontSize: '0.82rem' }}>
        {hint}
      </Typography>
      {footnote ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.45, color: alpha(ink, 0.58), lineHeight: 1.2, fontSize: '0.68rem' }}>
          {footnote}
        </Typography>
      ) : null}
    </Card>
  );
};

const SectionTitle = ({ children, brand }) => {
  const { pMid, pDark, pLite, ink } = brand;
  return (
    <Box sx={{ mb: 0.85 }}>
      <Typography variant="h6" sx={{ fontWeight: 900, color: ink }}>
        {children}
      </Typography>
      <Box
        sx={{
          height: 3,
          width: 156,
          borderRadius: 2,
          mt: 0.55,
          background: `linear-gradient(90deg, ${pDark}, ${pMid} 60%, ${pLite})`,
          '@media print': { background: pMid },
        }}
      />
    </Box>
  );
};

const SeverityProfilePanel = ({ severityStats, maxSeverityCount, highImpactCount, highImpactShare, brand }) => {
  const { ink } = brand;

  return (
    <Card className="avoid-break" sx={{ p: 2, border: `1px solid ${alpha(ink, 0.08)}`, boxShadow: 0, height: '100%' }}>
      <SectionTitle brand={brand}>Risk Profile (by severity)</SectionTitle>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.25 }}>
        <Chip
          size="small"
          label={`Critical + High: ${highImpactCount}`}
          sx={{
            bgcolor: alpha(sevColorMap.High, 0.12),
            color: sevColorMap.High,
            border: `1px solid ${alpha(sevColorMap.High, 0.22)}`,
            fontWeight: 800,
          }}
        />
        <Chip
          size="small"
          label={`${highImpactShare}% of validated findings`}
          sx={{
            bgcolor: alpha(brand.p, 0.08),
            color: brand.pMid,
            border: `1px solid ${alpha(brand.p, 0.18)}`,
            fontWeight: 700,
          }}
        />
      </Stack>

      <Box
        sx={{
          p: 1.45,
          borderRadius: 3,
          bgcolor: '#f8fafc',
          border: `1px solid ${alpha(ink, 0.06)}`,
        }}
      >
        <Box sx={{ position: 'relative', height: 212 }}>
          {[0.25, 0.5, 0.75, 1].map((ratio) => (
            <Box
              key={ratio}
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: `${ratio * 100}%`,
                borderTop: `1px dashed ${alpha(ink, 0.1)}`,
              }}
            />
          ))}

          <Stack direction="row" spacing={1.15} alignItems="flex-end" sx={{ position: 'relative', zIndex: 1, height: '100%' }}>
            {severityStats.map((item) => {
              const height = item.value
                ? `${Math.max(14, (item.value / maxSeverityCount) * 100)}%`
                : '8px';

              return (
                <Box
                  key={item.label}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      mb: 0.65,
                      fontWeight: 900,
                      color: item.value ? ink : alpha(ink, 0.42),
                    }}
                  >
                    {item.value}
                  </Typography>

                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 50,
                      height,
                      borderRadius: '16px 16px 10px 10px',
                      background: `linear-gradient(180deg, ${alpha(item.color, 0.78)} 0%, ${item.color} 100%)`,
                      border: `1px solid ${alpha(item.color, 0.2)}`,
                      boxShadow: `0 12px 24px ${alpha(item.color, 0.18)}`,
                    }}
                  />

                  <Typography variant="caption" sx={{ mt: 0.65, fontWeight: 800, color: alpha(ink, 0.78) }}>
                    {item.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: alpha(ink, 0.5) }}>
                    {item.share}%
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Box>
    </Card>
  );
};

const SeverityMixPanel = ({ severityStats, totalFindings, highImpactCount, highImpactShare, brand }) => {
  const { ink } = brand;
  const visibleStats = severityStats.filter((item) => item.value > 0);

  return (
    <Card className="avoid-break" sx={{ p: 1.8, border: `1px solid ${alpha(ink, 0.08)}`, boxShadow: 0, height: '100%' }}>
      <SectionTitle brand={brand}>Severity Mix</SectionTitle>

      <Box
        sx={{
          p: 1.2,
          borderRadius: 3,
          bgcolor: '#f8fafc',
          border: `1px solid ${alpha(ink, 0.06)}`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 0.95 }}>
          <Box>
            <Typography variant="caption" sx={{ color: alpha(ink, 0.62), fontWeight: 800, letterSpacing: 0.5 }}>
              VALIDATED FINDINGS
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1, color: ink, mt: 0.2 }}>
              {totalFindings}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: alpha(ink, 0.62), fontWeight: 800, letterSpacing: 0.5 }}>
              HIGH-IMPACT SHARE
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 900, color: sevColorMap.High, lineHeight: 1.1, mt: 0.2 }}>
              {highImpactShare}%
            </Typography>
            <Typography variant="caption" sx={{ color: alpha(ink, 0.58) }}>
              {highImpactCount} critical/high
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction="row"
          sx={{
            height: 14,
            borderRadius: 999,
            overflow: 'hidden',
            bgcolor: alpha(ink, 0.07),
            mb: 1,
          }}
        >
          {(visibleStats.length ? visibleStats : severityStats.slice(0, 1)).map((item) => (
            <Box
              key={item.label}
              sx={{
                flex: visibleStats.length ? Math.max(item.value, 1) : 1,
                bgcolor: visibleStats.length ? item.color : alpha(ink, 0.14),
              }}
            />
          ))}
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 0.75,
          }}
        >
          {severityStats.map((item) => (
            <Box
              key={item.label}
              sx={{
                p: 0.72,
                borderRadius: 2,
                bgcolor: alpha(item.color, 0.06),
                border: `1px solid ${alpha(item.color, 0.14)}`,
                minWidth: 0,
              }}
            >
              <Stack spacing={0.15} alignItems="center" sx={{ textAlign: 'center' }}>
                <Stack direction="row" spacing={0.6} alignItems="center" justifyContent="center" sx={{ minWidth: 0 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color, flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ fontWeight: 800, color: alpha(ink, 0.76), whiteSpace: 'nowrap' }}>
                    {item.label}
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: ink, lineHeight: 1.1 }}>
                  {item.value}
                </Typography>
                <Typography variant="caption" sx={{ color: alpha(ink, 0.48), lineHeight: 1 }}>
                  findings
                </Typography>
              </Stack>
            </Box>
          ))}
        </Box>
      </Box>
    </Card>
  );
};

const TopFindingRow = ({ finding, index, brand }) => {
  const { ink } = brand;
  const tone = sevColorMap[finding.severity] || brand.pMid;

  return (
    <Box
      data-exec-top-finding-row="true"
      sx={{
        p: 1.45,
        borderRadius: 2.5,
        border: `1px solid ${alpha(tone, 0.16)}`,
        background: `linear-gradient(180deg, ${alpha(tone, 0.07)} 0%, rgba(255,255,255,0.98) 55%)`,
      }}
    >
      <Stack direction="row" spacing={1.35} alignItems="flex-start">
        <Avatar
          sx={{
            width: 34,
            height: 34,
            bgcolor: alpha(tone, 0.12),
            color: tone,
            border: `1px solid ${alpha(tone, 0.18)}`,
            fontWeight: 900,
            fontSize: 15,
          }}
        >
          {index + 1}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 800, color: ink, lineHeight: 1.35 }}>
            {finding.title}
          </Typography>

          <Stack direction="row" spacing={0.9} useFlexGap flexWrap="wrap" sx={{ mt: 0.85, rowGap: 0.75 }}>
            {finding.asset ? (
              <Chip
                size="small"
                label={finding.asset}
                sx={{
                  maxWidth: 240,
                  bgcolor: alpha(brand.p, 0.08),
                  color: brand.pMid,
                  border: `1px solid ${alpha(brand.p, 0.16)}`,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            ) : null}
            <Typography variant="caption" sx={{ color: alpha(ink, 0.58), fontWeight: 700, alignSelf: 'center' }}>
              Assessment-confirmed finding
            </Typography>
          </Stack>
        </Box>

        <Chip
          size="small"
          label={finding.severity}
          sx={{
            bgcolor: tone,
            color: '#fff',
            fontWeight: 800,
            borderRadius: '999px',
            alignSelf: 'center',
          }}
        />
      </Stack>
    </Box>
  );
};

const ScopeSnippet = ({ items = [], totals = {}, brand }) => {
  const { pMid, ink } = brand;
  const meta = {
    domain: { label: 'Domain', color: pMid },
    ip:     { label: 'IP',     color: '#7c4dff' },
    cidr:   { label: 'CIDR',   color: '#00bfa5' },
    unknown:{ label: 'Other',  color: '#9e9e9e' },
  };

  return (
    <Card
      data-exec-block="scope"
      className="avoid-break"
      sx={{
        mt: 0.5,
        p: 2.5,
        mb: 3,
        border: `1px solid ${alpha(ink, 0.08)}`,
        boxShadow: 0,
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
        breakBefore: 'page',
        pageBreakBefore: 'always',
      }}
    >
      <SectionTitle brand={brand}>Scope (excerpt)</SectionTitle>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
        {Object.entries(totals).map(([k,v]) => (
          <Chip
            key={k}
            size="small"
            label={`${meta[k]?.label || k}: ${v || 0}`}
            sx={{
              bgcolor: alpha(meta[k]?.color || '#999', 0.12),
              color: meta[k]?.color || '#333',
              border: `1px solid ${alpha(meta[k]?.color || '#999', 0.25)}`,
              fontWeight: 700,
            }}
          />
        ))}
      </Stack>

      {items.length ? (
        <List dense disablePadding>
          {items.map((a, i) => (
            <ListItem key={`${a.raw}-${i}`} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 30, color: alpha(ink, 0.6) }}>{typeIcon(a.type)}</ListItemIcon>
              <ListItemText
                primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 700, color: ink } }}
                secondaryTypographyProps={{ variant: 'caption', sx: { color: alpha(ink, 0.6) } }}
                primary={a.type === 'domain' ? (a.value || a.raw) : a.value}
                secondary={a.type === 'domain' && a.wasWildcard ? `Wildcard covers *.${a.value}` : null}
              />
              {a.type === 'domain' ? (
                <Box component="a" href={`https://${a.value}`} target="_blank" rel="noreferrer" sx={{ ml: 1, color: pMid }}>
                  <LinkIcon fontSize="small" />
                </Box>
              ) : null}
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" sx={{ color: alpha(ink, 0.7) }}>
          No explicit scope provided.
        </Typography>
      )}
    </Card>
  );
};

const MainBody = ({
  totalFindings,
  openPorts,
  totalAssets,
  severityStats,
  maxSeverityCount,
  highImpactCount,
  highImpactShare,
  topFindings,
  paras,
  scopePreview,
  scopeTotals,
  brand,
}) => {
  const { p, pMid, pLite, ink } = brand;

  return (
    <>
      {/* KPIs */}
      <Grid container spacing={1.5} sx={{ mb: 2.1 }} className="avoid-break" data-exec-block="kpis">
        <Grid item xs={12} md={4} className="exec-kpi-item">
          <Kpi
            label="Findings"
            value={totalFindings}
            hint="Validated issues identified during the assessment."
            footnote={`${highImpactCount} critical/high issues currently drive material risk.`}
            icon={<WarningAmberIcon fontSize="small" />}
            accent={sevColorMap.High}
            brand={brand}
          />
        </Grid>
        <Grid item xs={12} md={4} className="exec-kpi-item">
          <Kpi
            label="Open Ports"
            value={openPorts}
            hint="Internet-facing services confirmed during reconnaissance."
            footnote="Reflects observed external exposure at the time of testing."
            icon={<RadarIcon fontSize="small" />}
            accent={brand.pMid}
            brand={brand}
          />
        </Grid>
        <Grid item xs={12} md={4} className="exec-kpi-item">
          <Kpi
            label="Assets"
            value={totalAssets}
            hint="Scoped domains, hosts, and perimeter assets reviewed."
            footnote="Inventory was refined through enumeration and service discovery."
            icon={<TimelineIcon fontSize="small" />}
            accent="#0ea5e9"
            brand={brand}
          />
        </Grid>
      </Grid>

      {/* Risk visuals */}
      <Grid container spacing={2} sx={{ mb: 2.4 }} data-exec-block="risk-visuals">
        <Grid item xs={12} md={7} className="exec-risk-primary">
          <SeverityProfilePanel
            severityStats={severityStats}
            maxSeverityCount={maxSeverityCount}
            highImpactCount={highImpactCount}
            highImpactShare={highImpactShare}
            brand={brand}
          />
        </Grid>

        <Grid item xs={12} md={5} className="exec-risk-secondary">
          <SeverityMixPanel
            severityStats={severityStats}
            totalFindings={totalFindings}
            highImpactCount={highImpactCount}
            highImpactShare={highImpactShare}
            brand={brand}
          />
        </Grid>
      </Grid>

      {/* Scope (compact) */}
      <ScopeSnippet items={scopePreview} totals={scopeTotals} brand={brand} />

      {/* Top Findings */}
      {topFindings.length > 0 && (
        <Card
          data-exec-top-findings-card="true"
          className="avoid-break"
          sx={{
            p: 2.5,
            mb: 3,
            border: `1px solid ${alpha(ink, 0.08)}`,
            boxShadow: 0,
            breakBefore: 'page',
            pageBreakBefore: 'always',
          }}
        >
          <Box data-exec-top-findings-intro>
            <SectionTitle brand={brand}>Top Findings (by severity)</SectionTitle>
            <Typography variant="body2" sx={{ color: alpha(ink, 0.72), mb: 1.5, lineHeight: 1.55 }}>
              The items below represent the most significant validated findings based on severity and likely business impact.
            </Typography>
          </Box>
          <Stack spacing={1.15}>
            {topFindings.map((f, index) => (
              <TopFindingRow key={f.id} finding={f} index={index} brand={brand} />
            ))}
          </Stack>
        </Card>
      )}

      {/* Executive Summary */}
      {paras.length > 0 && (
        <Card
          data-exec-summary-card="true"
          className="exec-summary-card"
          sx={{
            p: 2.5,
            mt: 3,
            border: `1px solid ${alpha(ink, 0.08)}`,
            boxShadow: 0,
            breakInside: 'auto',
            pageBreakInside: 'auto',
            position: 'relative',
            overflow: 'hidden',
            bgcolor: '#fff',
          }}
        >
          {/* subtle corner brackets */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              '&::before, &::after': {
                content: '""',
                position: 'absolute',
                width: 26,
                height: 26,
                border: `2px solid ${alpha(p, 0.4)}`,
              },
              '&::before': { top: 12, left: 12, borderRight: 'none', borderBottom: 'none', borderRadius: '6px 0 0 0' },
              '&::after': { bottom: 12, right: 12, borderLeft: 'none', borderTop: 'none', borderRadius: '0 0 6px 0' },
            }}
          />
          <Box data-exec-summary-intro>
            <SectionTitle brand={brand}>Executive Summary</SectionTitle>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5, color: alpha(ink, 0.75) }}>
              <InsightsIcon sx={{ fontSize: 18, color: pMid }} />
              <Typography variant="caption">Key findings and recommended actions at a glance.</Typography>
            </Stack>
          </Box>
          <div className="exec-summary">
            {paras.map((pText, i) => (
              <Typography
                key={i}
                data-exec-summary-block="true"
                variant="body2"
                sx={{
                  mb: 1.2,
                  lineHeight: 1.65,
                  whiteSpace: 'pre-wrap',
                  color: ink,
                  breakInside: 'auto',
                  pageBreakInside: 'auto',
                  overflowWrap: 'anywhere',
                }}
              >
                {pText}
              </Typography>
            ))}
          </div>
          <Box data-exec-summary-footer>
          {/* micro rail */}
          <Divider
            sx={{
              mt: 1.5,
              borderColor: alpha(ink, 0.08),
              '&::after': {
                content: '""',
                display: 'block',
                height: 3,
                width: 140,
                mt: 1,
                borderRadius: 2,
                background: `linear-gradient(90deg, ${p}, ${pLite})`,
              },
              '@media print': { '&::after': { background: pMid } },
            }}
          />
          <Stack direction="row" spacing={1.25} sx={{ mt: 1.25, alignItems: 'center', color: alpha(ink, 0.75) }}>
            <TimelineIcon sx={{ fontSize: 18, color: pMid }} />
            <Typography variant="caption">
              Flow: <strong>Discover → Exploit → Analyze → Report</strong>
            </Typography>
          </Stack>
          </Box>
        </Card>
      )}
    </>
  );
};

export default ExecutiveReport;
